import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getResources, getResourceCategories, createResource, deleteResource } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, FolderOpenIcon, ArrowDownTrayIcon, LinkIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-lg">{children}</div>
    </div>
  );
}

export default function ResourcesPage() {
  const { isFellow, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', external_url: '', category_id: '' });
  const [file, setFile] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['resources', search, catFilter],
    queryFn: () => getResources({ search: search || undefined, category_id: catFilter || undefined }),
  });

  const { data: catData } = useQuery({ queryKey: ['resource-categories'], queryFn: getResourceCategories });

  const createMut = useMutation({
    mutationFn: (fd) => createResource(fd),
    onSuccess: () => { qc.invalidateQueries(['resources']); toast.success('Resource added!'); setShowModal(false); setFile(null); setForm({ title: '', description: '', external_url: '', category_id: '' }); },
    onError: () => toast.error('Failed to add resource'),
  });

  const deleteMut = useMutation({
    mutationFn: deleteResource,
    onSuccess: () => { qc.invalidateQueries(['resources']); toast.success('Deleted'); },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('title', form.title);
    if (form.description) fd.append('description', form.description);
    if (form.external_url) fd.append('external_url', form.external_url);
    if (form.category_id) fd.append('category_id', form.category_id);
    if (file) fd.append('file', file);
    createMut.mutate(fd);
  };

  const resources = data?.data || [];
  const categories = catData?.data || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Resource Hub</h1>
          <p className="text-gray-500 text-sm">Shared templates, guides, and documents</p>
        </div>
        {isFellow && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> Upload
          </button>
        )}
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-4 h-4 text-gray-500" />
          <input className="input pl-9 w-56" placeholder="Search resources..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input w-48" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="text-gray-400 text-sm">Loading...</div>
      ) : resources.length === 0 ? (
        <div className="card text-center py-12">
          <FolderOpenIcon className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">No resources found.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {resources.map((r) => (
            <div key={r.id} className="card flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-white text-sm truncate">{r.title}</div>
                  {r.category && <span className="badge bg-blue-900/40 text-blue-300 text-xs">{r.category.name}</span>}
                </div>
                {isAdmin && (
                  <button onClick={() => { if (window.confirm('Delete?')) deleteMut.mutate(r.id); }} className="text-red-400 hover:text-red-300 flex-shrink-0">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              {r.description && <p className="text-xs text-gray-500 line-clamp-2">{r.description}</p>}
              <div className="text-xs text-gray-600 mt-auto">
                {r.uploaded_by?.full_name} · {format(new Date(r.created_at), 'dd MMM yyyy')}
                {r.file_size_kb && <span> · {(r.file_size_kb / 1024).toFixed(1)}MB</span>}
              </div>
              <div className="flex gap-2">
                {r.file_url && (
                  <a href={r.file_url} download className="flex items-center gap-1 text-xs text-primary-400 hover:text-primary-300">
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Download ({r.download_count})
                  </a>
                )}
                {r.external_url && (
                  <a href={r.external_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300">
                    <LinkIcon className="w-3.5 h-3.5" /> Open Link
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Upload Resource</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Category</label>
                <select className="input" value={form.category_id} onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))}>
                  <option value="">None</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-500 block mb-1">Description</label>
                <textarea className="input resize-none" rows={2} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Upload File</label>
                <input type="file" className="input text-xs" onChange={(e) => setFile(e.target.files[0])} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Or External URL</label>
                <input className="input" placeholder="https://..." value={form.external_url} onChange={(e) => setForm(f => ({ ...f, external_url: e.target.value }))} /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>
                  {createMut.isPending ? 'Uploading...' : 'Upload'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
